import { Article, Sponsor } from '@blockworks/platform/api/strapi';
import { Bit } from '@blockworks/ui/bit';
import { ExpandableContent, Image, Link } from '@blockworks/ui/components';
import { LockUnlockedIcon } from '@blockworks/ui/icon';

interface ArticleFeaturedSectionProps {
    featuredImage?: Article['featuredImage'];
    isFree?: boolean | null;
    sponsors?: Sponsor[] | null;
}
const getSponsorString = (sponsors: Sponsor[]) => {
    const lastSponsor = sponsors.length - 1;
    const commaSeparate = sponsors.length > 2;
    return sponsors.reduce((str: string, sponsor, idx) => {
        const isLastSponsor = lastSponsor === idx;
        const isFirstSponsor = idx === 0;

        if (isFirstSponsor) {
            return sponsor.name;
        }
        if (!isLastSponsor && commaSeparate) {
            return `${str}, ${sponsor.name}`;
        }
        if (isLastSponsor && !commaSeparate) {
            return `${str} and ${sponsor.name}`;
        }
        return `${str}, and ${sponsor.name}`;
    }, '');
};

const renderDisclaimer = (sponsors: Sponsor[]) => {
    const sponsorString = getSponsorString(sponsors);

    return `This research report has been funded by ${sponsorString}. By providing this disclosure, we aim to ensure that the research reported in this document 
    is conducted with objectivity and transparency.  Blockworks Research makes the following disclosures: 1) Research Funding: The research reported in this document has been funded by ${sponsorString}.  
    The sponsor may have input on the content of the report, but Blockworks Research maintains editorial control over the final report to retain data accuracy and objectivity.  
    All published reports by Blockworks Research are reviewed by internal independent parties to prevent bias.  2) Researchers submit financial conflict of interest (FCOI) 
    disclosures on a monthly basis that are reviewed by appropriate internal parties.  Readers are advised to conduct their own independent research and seek advice of
    qualified financial advisor before making investment decisions.`;
};

export const ArticleFeaturedSection = ({ featuredImage, sponsors, isFree }: ArticleFeaturedSectionProps) => {
    const showDisclaimer = isFree && sponsors?.length !== 0;

    if (!featuredImage) {
        return null;
    }

    const baseFeaturedProps = {
        width: featuredImage?.width,
        height: featuredImage?.height,
        src: featuredImage?.url,
    };

    if (featuredImage && !showDisclaimer) {
        return <Image borderRadius="lg" cover w="full" alt={featuredImage?.alternativeText} {...baseFeaturedProps} />;
    }

    const logos = sponsors?.map(({ id, name, logo, url }) => {
        return (
            <Link.Base key={id} href={url} target="_blank">
                <Image
                    className="contrast-0 aspect-auto w-auto h-5"
                    src={logo.url}
                    width={logo.width}
                    height={logo.height}
                    alt={name ?? ''}
                />
            </Link.Base>
        );
    });

    return (
        <Bit.div overflow="hidden" border={1} borderRadius="lg">
            <Bit.div display="flex" flexDirection="column" border="divide-y">
                <Image alt={featuredImage?.alternativeText} {...baseFeaturedProps} />
                <Bit.div display="flex" alignItems="center" gap={2} fontSize="sm" p={3} px={4}>
                    <LockUnlockedIcon size={1} /> Unlocked by {logos}
                </Bit.div>
                <Bit.div fontSize="xs" color="deselect" pt={2} px={4} pb={5}>
                    <ExpandableContent maxHeight={64}>{<Bit.p>{renderDisclaimer(sponsors!)}</Bit.p>}</ExpandableContent>
                </Bit.div>
            </Bit.div>
        </Bit.div>
    );
};
